import {
  DOMAIN_LABELS,
  RUBRIC_DOMAINS,
  UNCLASSIFIED_DOMAIN,
  domainLabel,
} from "@/components/test-cases/allTestCasesSearch";
import { isDropped } from "@/components/test-cases/dropState";
import type { ReferenceJob, ReferenceVersionList, TestCaseListItem } from "@/types";

/** One run is usually a few minutes at the default max effort, at most 10 (sams-reference-api.md §3). */
export const MINUTES_PER_CASE = 5;
export const PAGE_SIZES = [10, 20, 50] as const;
export const DEFAULT_PAGE_SIZE = 10;
/** Version lists read at once while the tab loads — each is one owner read. */
export const VERSION_READ_CONCURRENCY = 4;

/** What a row says in the 상태 column. `unknown` until its versions are read. */
export type RowStatus = "none" | "done" | "queued" | "running" | "failed" | "blocked" | "unknown";

export const STATUS_LABEL: Record<RowStatus, string> = {
  none: "미 생성",
  done: "생성 완료",
  queued: "대기 중",
  running: "생성 중",
  failed: "실패",
  blocked: "생성 불가",
  unknown: "확인 중",
};

/** The status chips above the table. `""` is everything. */
export type StatusFilter = "" | "done" | "running" | "failed" | "blocked";

export const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: "", label: "전체" },
  { value: "done", label: "생성 완료" },
  { value: "running", label: "생성 중" },
  { value: "failed", label: "실패" },
  { value: "blocked", label: "생성 불가" },
];

export const caseLabel = (tc: Pick<TestCaseListItem, "title" | "trigger_preview" | "test_case_id">) =>
  tc.title || tc.trigger_preview || tc.test_case_id.slice(0, 8);

export const domainKey = (tc: Pick<TestCaseListItem, "domain">) => tc.domain || UNCLASSIFIED_DOMAIN;

/** The KST calendar date (YYYY-MM-DD) of an instant. */
export function kstDate(at: string | number | Date): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Seoul" }).format(date);
}

/** "10/06 09:50" in KST. */
export function kstShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("month")}/${get("day")} ${get("hour")}:${get("minute")}`;
}

export function minutesSince(iso: string | null | undefined, now = Date.now()): number {
  if (!iso) return 0;
  const start = new Date(iso).getTime();
  return Number.isNaN(start) ? 0 : Math.max(0, Math.floor((now - start) / 60000));
}

/**
 * Why a TC cannot be given a Reference from this screen, or null.
 *
 * SAMS itself would run an inactive TC; this tab leaves retired ones out on
 * purpose, the way Batch Test does. A dropped TC fails at the worker
 * (REFERENCE_TC_DROPPED), an environment other than dev/prod is refused (422),
 * and a prod TC needs prod access (403) — said here before the click instead.
 */
export function blockedReason(tc: TestCaseListItem, prodAccess: boolean): string | null {
  if (isDropped(tc)) return "탈락한 TC";
  if (!tc.is_active) return "비활성";
  if (tc.source_environment !== "dev" && tc.source_environment !== "prod") {
    return `${tc.source_environment} 환경은 재현할 수 없음`;
  }
  if (tc.source_environment === "prod" && !prodAccess) return "prod 권한 필요";
  return null;
}

const isOpen = (job: ReferenceJob | undefined) => job?.status === "queued" || job?.status === "running";

/**
 * One row's state. The newest job this screen knows for the TC wins while it
 * is open; a failed one wins only when it is newer than the newest version
 * (a later success elsewhere settled it). Otherwise a version made today says
 * 생성 완료 — reviewed or not: passing it is the 검토 tab's job, not this one's.
 */
export function rowStatus({
  blocked,
  versions,
  job,
  today,
}: {
  blocked: string | null;
  versions: ReferenceVersionList | undefined;
  job: ReferenceJob | undefined;
  today: string;
}): RowStatus {
  if (blocked) return "blocked";
  if (isOpen(job)) return job!.status as "queued" | "running";
  const open = versions?.open_job;
  if (isOpen(open ?? undefined)) return open!.status as "queued" | "running";
  if (!versions) return "unknown";
  const madeAt = versions.items[0]?.created_at ?? null;
  if (job?.status === "failed" && (!madeAt || (job.finished_at ?? job.created_at) > madeAt)) return "failed";
  if (madeAt && kstDate(madeAt) === today) return "done";
  return "none";
}

/** Can be ticked: not blocked and not already on its way. */
export const isPickable = (status: RowStatus) => status !== "blocked" && status !== "queued" && status !== "running";

export function matchesStatus(status: RowStatus, filter: StatusFilter): boolean {
  if (!filter) return true;
  if (filter === "running") return status === "running" || status === "queued";
  return status === filter;
}

export function countByFilter(statuses: readonly RowStatus[]): Record<StatusFilter, number> {
  const counts: Record<StatusFilter, number> = { "": statuses.length, done: 0, running: 0, failed: 0, blocked: 0 };
  for (const status of statuses) {
    for (const { value } of STATUS_FILTERS) if (value && matchesStatus(status, value)) counts[value] += 1;
  }
  return counts;
}

/** The five scored Categories as All Test Cases lists them, then proposed slugs, then 미분류. */
export function domainOrder(cases: readonly Pick<TestCaseListItem, "domain">[]): string[] {
  const present = new Set(cases.map(domainKey));
  const proposals = [...present]
    .filter((key) => key !== UNCLASSIFIED_DOMAIN && !RUBRIC_DOMAINS.includes(key))
    .sort();
  return [...RUBRIC_DOMAINS, ...proposals, UNCLASSIFIED_DOMAIN].filter((key) => present.has(key));
}

export const DOMAIN_FILTER_OPTIONS = [
  ...RUBRIC_DOMAINS.map((key) => ({ value: key, label: DOMAIN_LABELS[key] ?? key })),
  { value: UNCLASSIFIED_DOMAIN, label: domainLabel(UNCLASSIFIED_DOMAIN) },
];

/** Grouped by Category; the list's own order is kept inside each group. */
export function groupedOrder<T extends Pick<TestCaseListItem, "domain">>(cases: readonly T[]): T[] {
  const rank = new Map(domainOrder(cases).map((key, index) => [key, index]));
  return cases
    .map((tc, index) => ({ tc, index }))
    .sort((a, b) => rank.get(domainKey(a.tc))! - rank.get(domainKey(b.tc))! || a.index - b.index)
    .map(({ tc }) => tc);
}

export type PageSlice<T> = { rows: T[]; page: number; pages: number; from: number };

export function pageOf<T>(rows: readonly T[], page: number, size: number): PageSlice<T> {
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const current = Math.min(Math.max(1, page), pages);
  const from = (current - 1) * size;
  return { rows: rows.slice(from, from + size), page: current, pages, from };
}

/** 1 … 4 5 6 … 12 — first, last and the current page's neighbours. */
export function pageNumbers(page: number, pages: number): Array<number | "gap"> {
  const out: Array<number | "gap"> = [];
  for (let n = 1; n <= pages; n += 1) {
    if (n === 1 || n === pages || Math.abs(n - page) <= 1) out.push(n);
    else if (out[out.length - 1] !== "gap") out.push("gap");
  }
  return out;
}

/**
 * What a start would request and what it would leave out. SAMS has no
 * once-a-day rule — every request is a new version — so the screen keeps it:
 * a TC whose newest version is from today is skipped unless asked again.
 */
export function startTargets<T extends Pick<TestCaseListItem, "test_case_id">>(
  selected: ReadonlySet<string>,
  cases: readonly T[],
  statusOf: (id: string) => RowStatus,
  regenerate: boolean,
): { run: T[]; skip: T[] } {
  const run: T[] = [];
  const skip: T[] = [];
  for (const tc of cases) {
    if (!selected.has(tc.test_case_id)) continue;
    const status = statusOf(tc.test_case_id);
    if (!isPickable(status)) continue;
    if (status === "done" && !regenerate) skip.push(tc);
    else run.push(tc);
  }
  return { run, skip };
}

// --- Jobs this browser started ---------------------------------------------
// SAMS keeps a job for 7 days but has no "list jobs" route, so the ids this
// browser started are remembered to show the progress panel after a reload.
// A per-viewer convenience: losing it loses only that panel's history.

const JOBS_KEY = "sams.referenceJobs";
const JOBS_KEPT = 30;

export function rememberedJobIds(): string[] {
  try {
    const raw = JSON.parse(window.localStorage.getItem(JOBS_KEY) ?? "[]");
    return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

export function rememberJobIds(ids: readonly string[]) {
  try {
    window.localStorage.setItem(JOBS_KEY, JSON.stringify([...new Set(ids)].slice(0, JOBS_KEPT)));
  } catch {
    // Storage refused (private window): the panel just forgets on reload.
  }
}

/** Maps over `items` with at most `limit` promises in flight. */
export async function mapLimited<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      out[index] = await fn(items[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}
